// The confirmation windows' twin of the server's plan arithmetic (OH-205 spec §3.6, §4): convertMinor
// (settle.go), forwardAtPlan and withinFXBand (income.go), and the verdicts built on them — what a row
// highlights, whether Confirm needs a second press, what Confirm does to the row. Pure: no React, no I/O.
//
// The window and the server must read a row the same way, so every decision here is exact: products and
// the 5% comparisons run in BigInt — no float, no rounded percentage — and the only divisions are the ones
// that exist already: convertMinor (the server's own, half-up) and deviationBp (display). Amounts are
// integer minor units (lib/money); a fraction or NaN is a bug in the caller, and BigInt() throws on it.
import { exponent } from './money';

/** OH-205 §3.6/§4: the FX band and the second press share one number. */
export const BAND_PCT = 5;
const BAND_PCT_BIG = BigInt(BAND_PCT);

/** money.RateMicroEUR: EUR's rate is 1 000 000 whether or not the rates map holds it. */
const EUR_MICRO = 1_000_000n;

/**
 * factResidueTolerance (income.go): how far a payment in another currency may miss the plan and still be
 * at plan — ONE MINOR UNIT, measured in either currency.
 */
const AT_PLAN_TOLERANCE = 1;

/** NBS rate of `currency` in micro-units per 1 EUR; null when `rates` holds no usable one (the DB keeps them > 0). */
function rateMicro(currency: string, rates: Readonly<Record<string, number>>): bigint | null {
  if (currency === 'EUR') return EUR_MICRO;
  const rate = rates[currency];
  return rate !== undefined && Number.isSafeInteger(rate) && rate > 0 ? BigInt(rate) : null;
}

/** lib/money `exponent`, which throws for a currency outside its table, as null. */
function minorDigits(currency: string): number | null {
  try {
    return exponent(currency);
  } catch {
    return null;
  }
}

/**
 * convertMinor (settle.go) in the window: amount × rate_to × 10^e_to / (rate_from × 10^e_from),
 * half-up, in BigInt; EUR is 1 000 000 whether or not `rates` holds it; one currency → identity;
 * null when a rate or an exponent (lib/money `exponent`) is unknown. Positive amounts only, as on the server.
 *
 * Truncating division, then half-up on the remainder: the server's QuoRem literally, so a non-positive
 * amount comes out as it does there. A rate that is not a positive integer counts as unknown, and a result
 * a number cannot hold exactly (past the safe integers) is null, as the server refuses one past int64 — no
 * currency or rate the data can hold makes it throw.
 */
export function convertMinor(amount: number, from: string, to: string, rates: Readonly<Record<string, number>>): number | null {
  if (from === to) return amount;
  const rateFrom = rateMicro(from, rates);
  const rateTo = rateMicro(to, rates);
  const digitsFrom = minorDigits(from);
  const digitsTo = minorDigits(to);
  if (rateFrom === null || rateTo === null || digitsFrom === null || digitsTo === null) return null;
  const num = BigInt(amount) * rateTo * 10n ** BigInt(digitsTo);
  const den = rateFrom * 10n ** BigInt(digitsFrom);
  let q = num / den;
  if (2n * (num % den) >= den) q += 1n;
  const converted = Number(q);
  return Number.isSafeInteger(converted) ? converted : null;
}

/**
 * The server's at-plan rule (forwardAtPlan, income.go) in the window [PL-02]: one currency — exact
 * equality; another — forward |paid − planned| ≤ 1 account unit OR backward |round(paid → target) − remaining|
 * ≤ 1 target unit. A missing rate → false (the preview blocks such a request anyway).
 */
export function atPlan(o: {
  paidMinor: number;
  plannedMinor: number;
  accountCurrency: string;
  remainingMinor: number;
  targetCurrency: string;
  rates: Readonly<Record<string, number>>;
}): boolean {
  if (o.accountCurrency === o.targetCurrency) return o.paidMinor === o.plannedMinor;
  if (o.paidMinor <= 0 || o.remainingMinor <= 0) return false;
  // The server needs both rates before either half can answer (round(remaining → account) feeds the forward
  // half) and refuses the request without them: a missing rate is false even where the forward half would
  // hold on `plannedMinor` alone — that number IS the server's round(remaining → account).
  const back = convertMinor(o.paidMinor, o.accountCurrency, o.targetCurrency, o.rates);
  if (back === null) return false;
  return (
    Math.abs(o.paidMinor - o.plannedMinor) <= AT_PLAN_TOLERANCE || Math.abs(back - o.remainingMinor) <= AT_PLAN_TOLERANCE
  );
}

/** What the window compares for one row. */
export interface PlanComparison {
  paidMinor: number; // the payment, account currency (with settled_minor: the override, the row's currency)
  plannedMinor: number; // the plan in that same currency (planned_minor; with settled_minor: the remaining plan)
  crossCurrency: boolean; // account and target currencies differ (by code); false for the override comparison
  atPlan: boolean; // atPlan(...) — the server closes the row at plan by the old tolerance
}

/** The two sides of the band comparison, exact: |paid − planned| × 100 against planned × BAND_PCT. */
function bandSides(paidMinor: number, plannedMinor: number): { dist: bigint; limit: bigint } {
  const gap = BigInt(paidMinor) - BigInt(plannedMinor);
  return { dist: (gap < 0n ? -gap : gap) * 100n, limit: BigInt(plannedMinor) * BAND_PCT_BIG };
}

/** |paid − planned| × 100 ≤ planned × 5, exact (BigInt); false for planned ≤ 0. */
export function withinBand(paidMinor: number, plannedMinor: number): boolean {
  if (paidMinor <= 0 || plannedMinor <= 0) return false; // as withinFXBand: a non-positive payment or plan is never inside
  const { dist, limit } = bandSides(paidMinor, plannedMinor);
  return dist <= limit;
}

/** §4: highlighted ⇔ the server would not close the row at plan by the old tolerance. */
export function isHighlighted(c: PlanComparison): boolean {
  return !c.atPlan;
}

/** §4: highlighted AND |paid − planned| × 100 > planned × 5. A plan that is not positive has no band: any difference presses. */
export function needsSecondPress(c: PlanComparison): boolean {
  if (!isHighlighted(c)) return false;
  const { dist, limit } = bandSides(c.paidMinor, c.plannedMinor);
  return dist > limit;
}

/** §4, F2: any row needing it, or the batch shortfall over 5% of the rows' planned total. */
export function batchNeedsSecondPress(rows: readonly PlanComparison[], shortfallMinor: number): boolean {
  if (rows.some(needsSecondPress)) return true;
  const planned = rows.reduce((sum, r) => sum + BigInt(r.plannedMinor), 0n);
  return BigInt(shortfallMinor) * 100n > planned * BAND_PCT_BIG;
}

/**
 * Signed basis points for display only: round half away from zero of (paid − planned) × 10000 / planned.
 * In BigInt, not Math.round: that rounds a half toward +∞ (−178.5 → −178) and can return −0, which Intl
 * prints as "-0". 0 for a plan that is not positive — there is no percentage of nothing.
 */
export function deviationBp(paidMinor: number, plannedMinor: number): number {
  if (plannedMinor <= 0) return 0;
  const num = (BigInt(paidMinor) - BigInt(plannedMinor)) * 10_000n;
  const den = BigInt(plannedMinor);
  const abs = num < 0n ? -num : num;
  let q = abs / den;
  if (2n * (abs % den) >= den) q += 1n;
  return Number(num < 0n ? -q : q);
}

export type SettleMode = 'restate' | 'partial' | 'fee';
export type SettleOutcome =
  | { kind: 'none' } // the server closes it at plan by the old tolerance
  // the plan becomes newPlanMinor: settledSoFarMinor, settled earlier, plus this payment — the target's currency
  | { kind: 'restate'; newPlanMinor: number; settledSoFarMinor: number }
  | { kind: 'partial'; restMinor: number } // stays in the queue — the target's currency
  | { kind: 'fee'; feeMinor: number } // booked as a fee — the account currency
  | { kind: 'fx'; deltaMinor: number }; // closes at plan, the rest is an exchange difference — the account currency

/**
 * §4: what Confirm does with a single row, in the server's order. remainingMinor — the target's remaining
 * plan; settledMinor — the payment converted into the target's currency (convertMinor; = paidMinor in one currency).
 * settledSoFarMinor — the target's own settled_minor before this payment, in its currency: a restate makes the row's
 * whole new plan of it plus this payment, as the server stores it.
 * mode — what the request carries [PL-R2-01]: 'fee' in the fee mode; the remainder only while the question
 * shows; otherwise 'restate', as the server reads a body without one (plan decision 11).
 */
export function settleOutcome(
  c: PlanComparison & { mode: SettleMode; remainingMinor: number; settledMinor: number; settledSoFarMinor: number },
): SettleOutcome {
  if (c.atPlan) return { kind: 'none' };
  if (c.mode === 'fee' && c.paidMinor < c.plannedMinor) {
    return { kind: 'fee', feeMinor: c.plannedMinor - c.paidMinor };
  }
  if (c.crossCurrency && c.mode !== 'partial' && withinBand(c.paidMinor, c.plannedMinor)) {
    return { kind: 'fx', deltaMinor: c.paidMinor - c.plannedMinor };
  }
  if (c.mode === 'partial' && c.settledMinor < c.remainingMinor) {
    return { kind: 'partial', restMinor: c.remainingMinor - c.settledMinor };
  }
  return { kind: 'restate', newPlanMinor: c.settledSoFarMinor + c.settledMinor, settledSoFarMinor: c.settledSoFarMinor };
}
